import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { ProductWithCatalog } from "@/types/content/product";
import { FundingOrganismType } from "@/types/content/registration/funding_organism";
import { handle_error } from "@/utils/wretch";
import { Outlet, RouteObject, defer, json } from "react-router-dom";

const ExternalProviderRoute: RouteObject = {
    path: "providers",
    element: (
        <Protected groups={["app_registration_admin", "team_finance", "team_external_provider"]} profiles={["system_admin"]}>
            <Outlet />
        </Protected>
    ),
    loader: async () => {
        const providers = await DataService.client.url(`/registration/sales/providers/`).get().json();
        return defer({ providers });
    },
    children: [
        {
            path: ":providerId",
            loader: async ({ params }) => {
                const providerId = params.providerId;
                const provider = await DataService.client.url(`/registration/sales/providers/${providerId}/`).get().json();
                return { provider };
            },
            action: async ({ params, request }) => {
                const providerId = params.providerId;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                if (action) {
                    try {
                        const content = await DataService.client.url(`/registration/sales/providers/${providerId}/${action}/`).post(data).json();
                        return json({ success: true, content: content });
                    } catch (error) {
                        return handle_error(error);
                    }
                } else {
                    try {
                        const content = await DataService.client.url(`/registration/sales/providers/${providerId}/`).patch(data).json();
                        return json({ success: true, content: content });
                    } catch (error) {
                        return handle_error(error);
                    }
                }
            },
            children: [
                {
                    path: "funding_organism_options",
                    loader: async ({ params }) => {
                        const funding_organisms_ordering = ["DPC", "FIFPL", "OPCO", "CPF", "STRIPE", "ALMA", "BANK_TRANSFER"];
                        const providerId = params.providerId;
                        const funding_organisms = (await DataService.client.url(`/registration/sales/providers/${providerId}/funding_organisms/`).get().json()) as FundingOrganismType[];
                        let options: { value: string; label: string }[] = [];
                        const hasOpco = funding_organisms.some(fo => fo.type == "opco");
                        funding_organisms.map(fo => {
                            if (fo.code == "DPC") {
                                options.push({ value: "DPC", label: "DPC" });
                            } else if (fo.code == "FIFPL") {
                                options.push({ value: "FIFPL", label: "FIFPL" });
                            } else if (fo.code == "CPF") {
                                options.push({ value: "CPF", label: "CPF" });
                            } else if (["ALMA"].includes(fo.code)) {
                                if (options.filter(o => o.value == "ALMA").length == 0) options.push({ value: "ALMA", label: "CB professionnel" });
                            } else if (["BANK_TRANSFER"].includes(fo.code)) {
                                if (options.filter(o => o.value == "BANK_TRANSFER").length == 0) options.push({ value: "BANK_TRANSFER", label: "Virement professionnel" });
                            } else if (["STRIPE"].includes(fo.code)) {
                                if (options.filter(o => o.value == "STRIPE").length == 0) options.push({ value: "STRIPE", label: "CB particulier" });
                            }
                        });
                        if (hasOpco) {
                            options.push({ value: "OPCO", label: "OPCO" });
                        }
                        return options.sort((a, b) => funding_organisms_ordering.indexOf(a.value) - funding_organisms_ordering.indexOf(b.value));
                    }
                },
                {
                    path: "opco_funding_organism_options",
                    loader: async ({ params }) => {
                        const providerId = params.providerId;
                        const funding_organisms = (await DataService.client.url(`/registration/sales/providers/${providerId}/funding_organisms/`).get().json()) as FundingOrganismType[];
                        return funding_organisms
                            .filter(fo => fo.type == "opco")
                            .map(fo => ({ value: fo.code, label: fo.name }))
                            .sort((a, b) => a.label.localeCompare(b.label));
                    }
                },
                {
                    path: "products",
                    loader: async ({ params, request }) => {
                        const providerId = params.providerId;
                        const url = new URL(request.url);
                        const query = url.searchParams.toString();
                        const products = (await DataService.client
                            .url(`/registration/sales/providers/${providerId}/products/${query ? `?${query}` : ""}`)
                            .get()
                            .json()) as ProductWithCatalog[];
                        return products;
                    }
                }
            ]
        }
    ]
};
export default ExternalProviderRoute;
